// MCP clients (poc CT-012, architecture 7.4; HPG MCP Authorisation Standard v2.0, MCP-08 and MCP-23). Shared so
// the server (the checks, the refusals) and the Admin page (the register dialog, the switches) agree.
//
// Two kinds, and no client has a secret (MCP-08):
//   people  People connect it themselves (Claude, Claude Code, VS Code): a public client, identified by its
//           client ID (a tool-issued one, or its published identity's URL, poc CT-015), with exact redirect URIs
//           and PKCE.
//   app     An app with its own Entra identity: identified by its Entra application ID, no redirect URIs. It
//           records an owner (a person) and a purpose. poc CT-014: it calls the tool as itself with Entra's app-only
//           token, as its own principal (kind mcp_app, named "<client name> (app)").
//
// What a client can do (MCP-23, least privilege per client): Read is always on while the client is allowed;
// Write, and under it Modify and Delete, are switches the Admins set. A call needs both the person's permission
// and the client's switch.
//
// poc CT-015 (MCP-03, MCP-04): a people client identifies itself in one of two ways, chosen when it's registered:
//   client_id   the tool issues a client ID (mcp_<random>), and the Admins list its redirect URIs
//   published   a published identity: an https URL that is its client ID, where its Client ID Metadata Document
//               (draft-ietf-oauth-client-id-metadata-document) lists its name and redirect URIs, as Claude's
//               default "Use Claude's published identity" does. Only a registered URL is ever accepted (or fetched).

export const MCP_CLIENT_KINDS = ['people', 'app'] as const;
export type McpClientKind = (typeof MCP_CLIENT_KINDS)[number];

export const MCP_CLIENT_KIND_LABELS: Record<McpClientKind, string> = {
  people: 'People connect it themselves',
  app: 'An app with its own Entra identity',
};

/** How a people client identifies itself (poc CT-015). An app client is always `client_id`. */
export const MCP_CLIENT_IDENTIFICATIONS = ['client_id', 'published'] as const;
export type McpClientIdentification = (typeof MCP_CLIENT_IDENTIFICATIONS)[number];

export const MCP_CLIENT_IDENTIFICATION_LABELS: Record<McpClientIdentification, string> = {
  client_id: 'The tool issues a client ID',
  published: 'A published identity (URL)',
};

/** Claude's and Claude Code's published identities (their Client ID Metadata Documents), as the dev seed registers them. */
export const CLAUDE_PUBLISHED_IDENTITY_URL = 'https://claude.ai/oauth/mcp-oauth-client-metadata';
export const CLAUDE_CODE_PUBLISHED_IDENTITY_URL = 'https://claude.ai/oauth/claude-code-client-metadata';

const SECOND_MS = 1000;
const HOUR_MS = 60 * 60 * SECOND_MS;
const DAY_MS = 24 * HOUR_MS;

/** Fetching a published identity's document: one answer within this long (DNS included), never a redirect. */
export const PUBLISHED_IDENTITY_FETCH_TIMEOUT_MS = 5 * SECOND_MS;
/** A document larger than this is refused; reading stops past it. */
export const PUBLISHED_IDENTITY_MAX_BYTES = 10 * 1024;
/** A fetched document is used for its Cache-Control max-age (none, no-store or no-cache: 0), at most this long. */
export const PUBLISHED_IDENTITY_MAX_FRESH_MS = 24 * HOUR_MS;
/** When a refetch fails, the last good copy keeps working until this long after it was fetched. */
export const PUBLISHED_IDENTITY_LAST_GOOD_MS = 7 * DAY_MS;
/** A published identity's URL, like any short text in the data model. */
export const PUBLISHED_IDENTITY_URL_MAX = 1000;
/** A client's redirect URIs: at most this many, registered with a client ID or listed in a published document. */
export const REDIRECT_URIS_MAX = 20;
/** A client's name: the registered display name, or a published document's client_name (trimmed). */
export const CLIENT_NAME_MAX = 200;

/**
 * Why this can't be a published identity's URL (its client ID), or null. A Client ID Metadata Document's URL is
 * https, has a path, and holds no fragment, user name or password. The document must name it exactly as its
 * client_id, which the fetch checks.
 */
export function publishedIdentityUrlProblem(value: string): string | null {
  if (value.length > PUBLISHED_IDENTITY_URL_MAX) {
    return 'That address is too long.';
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return 'That isn’t a web address.';
  }
  if (url.protocol !== 'https:') {
    return 'A published identity’s address must start with https://.';
  }
  if (url.username || url.password) {
    return 'A published identity’s address can’t hold a user name or password.';
  }
  if (url.hash || value.includes('#')) {
    return 'A published identity’s address can’t have a fragment (#).';
  }
  if (url.pathname === '/') {
    return `A published identity’s address needs a path, such as ${CLAUDE_PUBLISHED_IDENTITY_URL}.`;
  }
  return null;
}

/** An app client's purpose ("Nightly triage report for the Service Desk"). */
export const MCP_CLIENT_PURPOSE_MAX = 500;

/**
 * The name an app client's own principal goes by (poc CT-014): ticket history and activity show "<client> (app)",
 * never "on behalf of". It follows the client's renames.
 */
export function appPrincipalName(clientDisplayName: string): string {
  return `${clientDisplayName} (app)`;
}

/** The Admins' switches. Read has none: it's always on while the client is allowed. */
export interface McpClientAccess {
  write: boolean;
  modify: boolean;
  delete: boolean;
}

/** What a new client starts with. */
export const READ_ONLY_ACCESS: McpClientAccess = Object.freeze({ write: false, modify: false, delete: false });

/** What every client registered before poc CT-012 kept. */
export const FULL_ACCESS: McpClientAccess = Object.freeze({ write: true, modify: true, delete: true });

/** The switch each MCP tool belongs to (poc CT-012: every tool belongs to exactly one). */
export const MCP_TOOL_ACCESS_LEVELS = ['read', 'write', 'modify', 'delete'] as const;
export type McpToolAccess = (typeof MCP_TOOL_ACCESS_LEVELS)[number];

/** A switch an Admin can turn on and off. */
export type McpClientSwitch = Exclude<McpToolAccess, 'read'>;

/** Modify and Delete need Write (the database checks the same). */
export function isValidAccess(access: McpClientAccess): boolean {
  return access.write || (!access.modify && !access.delete);
}

/**
 * The access after one switch is flipped, as the Admin page does it: Write on turns all three on; Write off turns
 * all three off; Modify and Delete change only themselves, and do nothing while Write is off.
 */
export function withSwitch(current: McpClientAccess, which: McpClientSwitch, on: boolean): McpClientAccess {
  if (which === 'write') {
    return on ? { ...FULL_ACCESS } : { ...READ_ONLY_ACCESS };
  }
  if (!current.write) {
    return { ...current };
  }
  return { ...current, [which]: on };
}

/** Whether the client's switches allow a tool of this level (both the person and the client must allow a call). */
export function clientMay(access: McpClientAccess, needed: McpToolAccess): boolean {
  return needed === 'read' || access[needed];
}

const ADMIN_CAN_ALLOW = 'An Admin can allow it in Admin > MCP clients.';

/** The refusal a call gets when the client's switch is off. */
export const CLIENT_ACCESS_REFUSALS: Record<McpClientSwitch, string> = {
  write: `This client can’t add comments. ${ADMIN_CAN_ALLOW}`,
  modify: `This client can’t modify tickets. ${ADMIN_CAN_ALLOW}`,
  delete: `This client can’t close, decline or merge tickets. ${ADMIN_CAN_ALLOW}`,
};
